import React, { useEffect, useState, useMemo } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink, ArrowUpDown } from 'lucide-react';
import { getArtists } from '../services/api';
import { Link } from 'react-router-dom';
import { PageLayout } from './layouts/PageLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

export const Artists = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name-asc');
  const [visibleElements, setVisibleElements] = useState([]);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await getArtists();
        setArtists(response.data);
      } catch (error) {
        console.error('Error fetching artists:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArtists();
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll-based animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [artists]);

  const sortedArtists = useMemo(() => {
    const sorted = [...artists];
    
    switch (sortBy) {
      case 'name-asc':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'name-desc':
        return sorted.sort((a, b) => b.name.localeCompare(a.name));
      case 'date-desc':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'date-asc':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      default:
        return sorted;
    }
  }, [artists, sortBy]);

  if (loading) {
    return (
      <PageLayout primaryColor="orange" secondaryColor="green">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-2xl text-green-400 animate-pulse">Loading artists...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout primaryColor="orange" secondaryColor="green">
      <div id="artists" className="container mx-auto px-4 pt-20 md:pt-24 pb-16 md:pb-24 relative z-10">
          <div 
            className="text-center mb-12 md:mb-16 transition-all duration-700 ease-out"
            data-animate
            data-index="header"
            style={{
              opacity: visibleElements.includes('header') ? 1 : 0,
              transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
            }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 hover:scale-105 transition-transform duration-300 cursor-default inline-block leading-tight pb-2">
              Our Artists
            </h2>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-orange-500 to-green-500 mx-auto mb-4"></div>
            <p className="text-gray-400 text-base md:text-lg hover:text-gray-300 transition-colors duration-300">Meet the sonic architects</p>
          </div>

          {/* Sort Controls */}
          <div className="flex justify-end mb-8">
            <div className="flex items-center gap-3">
              <ArrowUpDown className="w-5 h-5 text-orange-400" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px] bg-black/50 border-orange-500/30 text-white hover:border-orange-500 transition-colors">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="bg-black/95 border-orange-500/30 text-white">
                  <SelectItem value="name-asc" className="hover:bg-orange-500/20 focus:bg-orange-500/20">Name A-Z</SelectItem>
                  <SelectItem value="name-desc" className="hover:bg-orange-500/20 focus:bg-orange-500/20">Name Z-A</SelectItem>
                  <SelectItem value="date-desc" className="hover:bg-orange-500/20 focus:bg-orange-500/20">Newest Added</SelectItem>
                  <SelectItem value="date-asc" className="hover:bg-orange-500/20 focus:bg-orange-500/20">Oldest Added</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {sortedArtists.map((artist, index) => (
              <Link key={artist.id} to={`/artists/${artist.id}`}>
                <Card 
                  className="bg-black/50 border-orange-500/30 overflow-hidden hover:border-orange-500 hover:shadow-2xl hover:shadow-orange-500/40 hover:-translate-y-2 transition-all duration-300 group cursor-pointer gpu-accelerated"
                  data-animate
                  data-index={`artist-${index}`}
                  style={{
                    opacity: visibleElements.includes(`artist-${index}`) ? 1 : 0,
                    transform: visibleElements.includes(`artist-${index}`) ? 'scale(1)' : 'scale(0.9)',
                    transition: `all 0.5s ease-out ${index * 0.05}s`
                  }}
                >
                  <div className="relative overflow-hidden bg-black/80">
                    <img
                      src={artist.image}
                      alt={artist.name}
                      referrerPolicy="no-referrer"
                      
                      className="w-full h-64 object-contain group-hover:scale-105 transition-transform duration-500 ease-out"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6 flex flex-col min-h-[160px]">
                    <div className="flex items-start justify-between mb-2 gap-2">
                      <h3 className="text-xl font-bold text-orange-300 group-hover:text-orange-400 group-hover:scale-105 transition-all duration-300 pb-1">
                        {artist.name}
                      </h3>
                      {artist.type && (
                        <div className="flex flex-wrap gap-1">
                          {(Array.isArray(artist.type) ? artist.type : [artist.type]).map((type) => (
                            <Badge 
                              key={type}
                              variant="outline" 
                              className="border-green-500/50 bg-green-500/10 text-green-400 text-xs px-2 py-0.5"
                            >
                              {type}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-gray-400 mb-3 line-clamp-2 group-hover:text-gray-300 transition-colors duration-300">{artist.bio}</p>
                    <div className="flex items-center justify-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 hover:border-green-500 rounded-md text-green-400 hover:text-green-300 transition-all duration-300 group/link mt-auto">
                      <ExternalLink className="w-4 h-4 group-hover/link:scale-110 transition-transform duration-300" />
                      More About Artist
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
      </div>
    </PageLayout>
  );
};
